import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import Login from "@/pages/Login";
import PendingApproval from "@/pages/PendingApproval";
import SetPassword from "@/pages/SetPassword";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import Marketing from "@/pages/Marketing";
import MarketingDetail from "@/pages/MarketingDetail";
import Analytics from "@/pages/Analytics";
import Reports from "@/pages/Reports";
import Settings from "@/pages/Settings";
import Sales from "@/pages/Sales";
import SalesClientDetail from "@/pages/SalesClientDetail";
import Layout from "@/components/Layout";
import "@/App.css";

const Protected = ({ children, adminOnly = false, roles = null, salesOnly = false }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-slate-500 text-sm tracking-wider uppercase" data-testid="loading-indicator">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.status === "pending") return <PendingApproval />;
  if (user.status === "disabled") return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  if (salesOnly && user.role !== "admin" && !user.incl_sales) return <Navigate to="/" replace />;
  return <Layout>{children}</Layout>;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <BrandingProvider>
          <AuthProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/" element={<Protected><Dashboard /></Protected>} />
              <Route path="/leads" element={<Protected><Leads /></Protected>} />
              <Route path="/leads/:id" element={<Protected><LeadDetail /></Protected>} />
              <Route path="/marketing" element={<Protected><Marketing /></Protected>} />
              <Route path="/marketing/:id" element={<Protected><MarketingDetail /></Protected>} />
              <Route path="/sales" element={<Protected salesOnly><Sales /></Protected>} />
              <Route path="/sales/client" element={<Protected salesOnly><SalesClientDetail /></Protected>} />
              <Route path="/analytics" element={<Protected roles={["admin", "manager"]}><Analytics /></Protected>} />
              <Route path="/reports" element={<Protected adminOnly><Reports /></Protected>} />
              <Route path="/settings" element={<Protected adminOnly><Settings /></Protected>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AuthProvider>
        </BrandingProvider>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </div>
  );
}

export default App;
